import { Button, FieldLabel } from "@/components/kit";
import type { ReportProductFilterDTO } from "@/modules/reports/domain/contracts";
import {
  useProductQuery,
  useProductsQuery,
} from "@/modules/products/presentation/api/queries";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";

const ALL_FILTER = "__all";

type ReportProductVariantFilterProps = {
  value: ReportProductFilterDTO;
  onChange: (next: ReportProductFilterDTO) => void;
  /** แถบ filter บางๆ ใน ReportsFilterBar */
  compact?: boolean;
};

export function ReportProductVariantFilter({
  value,
  onChange,
  compact = false,
}: ReportProductVariantFilterProps) {
  const products = useProductsQuery({ includeInactive: true });
  const productDetail = useProductQuery(value.productId ?? "");

  const productOptions = [
    { value: ALL_FILTER, label: "ທຸກສິນຄ້າ" },
    ...(products.data?.data ?? []).map(
      (product: { id: string; name: string }) => ({
        value: product.id,
        label: product.name,
      }),
    ),
  ];

  const variantOptions = [
    { value: ALL_FILTER, label: "ທຸກຕົວເລືອກ" },
    ...(productDetail.data?.variants ?? []).map(
      (variant: { id: string; name: string }) => ({
        value: variant.id,
        label: variant.name,
      }),
    ),
  ];

  const hasFilter = Boolean(value.productId || value.variantId);
  const variantValue =
    value.productId &&
    variantOptions.some((option) => option.value === (value.variantId || ALL_FILTER))
      ? value.variantId || ALL_FILTER
      : undefined;

  const fields = (
    <div
      className={
        compact
          ? "grid gap-2 sm:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_auto] sm:items-end"
          : "grid gap-3 sm:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)] sm:items-end"
      }
    >
      <div className="flex min-w-0 flex-col gap-1.5">
        {!compact ? (
          <FieldLabel className="text-muted-foreground text-xs">ສິນຄ້າ</FieldLabel>
        ) : null}
        <SimpleSelect
          value={value.productId || ALL_FILTER}
          onValueChange={(next) => {
            onChange({
              productId: next === ALL_FILTER ? undefined : next,
              variantId: undefined,
            });
          }}
          options={productOptions}
          placeholder="ເລືອກສິນຄ້າ"
          className="h-10 w-full"
        />
      </div>
      <div className="flex min-w-0 flex-col gap-1.5">
        {!compact ? (
          <FieldLabel className="text-muted-foreground text-xs">ຕົວເລືອກ</FieldLabel>
        ) : null}
        <SimpleSelect
          value={variantValue}
          onValueChange={(next) => {
            onChange({
              ...value,
              variantId: next === ALL_FILTER ? undefined : next,
            });
          }}
          disabled={!value.productId || variantOptions.length <= 1}
          options={variantOptions}
          placeholder={
            value.productId ? "ທຸກຕົວເລືອກ" : "ເລືອກສິນຄ້າກ່ອນ"
          }
          className="h-10 w-full"
        />
      </div>
      {compact && hasFilter ? (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="rounded-full"
          onClick={() => onChange({})}
        >
          ລ້າງ
        </Button>
      ) : null}
    </div>
  );

  if (compact) {
    return fields;
  }

  return (
    <section className="brand-card flex flex-col gap-3 p-4 sm:p-5">
      <div>
        <h2 className="font-display text-lg">ກອງສິນຄ້າ</h2>
        <p className="text-muted-foreground text-sm">
          ເລືອກສິນຄ້າ ແລະ ຕົວເລືອກເພື່ອດູຂໍ້ມູນແຍກໃນລາຍງານນີ້
        </p>
      </div>
      {fields}
      {hasFilter ? (
        <div className="flex justify-end">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="rounded-full"
            onClick={() => onChange({})}
          >
            ລ້າງຕົວກອງສິນຄ້າ
          </Button>
        </div>
      ) : null}
    </section>
  );
}
